"use client";

import { Badge, Box, HStack, IconButton, Text } from "@chakra-ui/react";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import GenericIcon, { type GenericIconType } from "../actions/GenericIcon";
import WithMenu from "../actions/WithMenu";
import WithMenuItem from "../actions/WithMenuItem";

export const CONTENT_ITEM_HIGHLIGHT_STYLES = {
  "& mark": {
    background: "var(--chakra-colors-moveon-warningLight)",
    color: "inherit",
    paddingInline: "0.125rem",
    borderRadius: "var(--chakra-radii-sm)",
    overflowWrap: "anywhere",
    wordBreak: "normal",
    hyphens: "auto",
  },
} as const;

export const CONTENT_ITEM_MENU_MIN_WIDTH = "60";
export const CONTENT_ITEM_HOVER_STYLES = {
  borderColor: "moveon.selection.hoverBorder",
} as const;
export const CONTENT_ITEM_TEXT_PROPS = {
  fontSize: { base: "sm", md: "md" },
  lineHeight: "tall",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  wordBreak: "normal",
  hyphens: "auto",
} as const;
export const CONTENT_ITEM_FOCUS_VISIBLE_STYLES = {
  borderColor: "moveon.primary",
  outlineWidth: "2px",
  outlineStyle: "solid",
  outlineColor: "moveon.primary",
  outlineOffset: "2px",
} as const;

export type ContentItemAction = {
  key: string;
  label: string;
  humanReadable: string;
  icon: GenericIconType;
  onClick: () => void | Promise<void>;
  color?: string;
  tone?: "default" | "danger";
  disabled?: boolean;
  emphasized?: boolean;
};

export function ContentItemMenuLabel({
  label,
  icon,
  color,
}: {
  label: string;
  icon: GenericIconType;
  color?: string;
}) {
  return (
    <HStack gap="2" align="center" color={color}>
      <GenericIcon icon={icon} size={iconSizePresets.menuItem} color={color} />
      <Text color={color}>{label}</Text>
    </HStack>
  );
}

export function ContentItemPreviewActions({
  actions,
  mobileActions = actions,
}: {
  actions: ContentItemAction[];
  mobileActions?: ContentItemAction[];
}) {
  return (
    <>
      <HStack
        display={{ base: "none", lg: "flex" }}
        gap="2"
        flexWrap="wrap"
        justify="flex-start"
        flexDirection="row-reverse"
        flexShrink={0}
      >
        {actions.map((action) => (
          <IconButton
            key={action.key}
            size="xs"
            variant="ghost"
            borderWidth="1"
            borderStyle="solid"
            borderColor={
              action.disabled
                ? "moveon.surface.border"
                : action.tone === "danger"
                  ? "moveon.dangerBorder"
                  : action.emphasized
                    ? (action.color ?? "moveon.primary")
                    : "moveon.surface.interactiveBorder"
            }
            color={
              action.disabled
                ? "moveon.text.muted"
                : action.tone === "danger"
                ? "moveon.danger"
                : (action.color ?? "moveon.surface.fg")
            }
            bg={action.emphasized ? "moveon.primaryLight" : "moveon.surface.bg"}
            outlineWidth={action.emphasized ? "1" : "0"}
            outlineStyle="solid"
            outlineColor={action.color ?? "moveon.primary"}
            onClick={action.onClick}
            h="8"
            minW="8"
            disabled={action.disabled}
            aria-label={action.humanReadable}
            title={action.humanReadable}
            _hover={{
              bg:
                action.tone === "danger"
                  ? "moveon.dangerLight"
                  : action.emphasized
                    ? "moveon.primaryLight"
                  : "moveon.surface.elevatedBg",
            }}
            _focusVisible={CONTENT_ITEM_FOCUS_VISIBLE_STYLES}
          >
            <GenericIcon
              icon={action.icon}
              size={iconSizePresets.inline}
              color={
                action.disabled
                  ? "moveon.text.muted"
                  : action.tone === "danger"
                  ? "moveon.danger"
                  : (action.color ?? "moveon.surface.fg")
              }
              humanReadable={action.humanReadable}
            />
          </IconButton>
        ))}
      </HStack>

      <Box display={{ base: "block", lg: "none" }} flexShrink={0} ms="auto">
        <WithMenu
          size="sm"
          placement="bottom"
          minWidth={CONTENT_ITEM_MENU_MIN_WIDTH}
        >
          {mobileActions.map((action) => (
            <WithMenuItem
              key={action.key}
              value={action.key}
              humanReadable={action.humanReadable}
              onClick={action.onClick}
              disabled={action.disabled}
              tone={action.tone === "danger" ? "error" : undefined}
            >
              <ContentItemMenuLabel
                label={action.label}
                icon={action.icon}
                color={
                  action.disabled
                    ? "moveon.text.muted"
                    : action.tone === "danger"
                      ? "moveon.danger"
                      : action.color
                }
              />
            </WithMenuItem>
          ))}
        </WithMenu>
      </Box>
    </>
  );
}

export function ContentItemUnassignedCallout({
  label = "Unassigned",
  message,
  mb,
}: {
  label?: string;
  message?: string;
  mb?: string;
}) {
  return (
    <HStack mb={mb} gap="2" align="center" flexWrap="wrap">
      <Badge
        bg="moveon.warningLight"
        color="moveon.warning"
        borderWidth="1"
        borderStyle="solid"
        borderColor="moveon.warningBorder"
      >
        {label}
      </Badge>
      {message && (
        <Text fontSize="xs" color="moveon.text.muted">
          {message}
        </Text>
      )}
    </HStack>
  );
}
